import { env } from '~/env'

/**
 * Public PlatformConfig switches the web reads from `GET /platform/flags`
 * (unauthenticated). Every reader must treat a failed fetch as the
 * fail-closed default below: a network blip must never expose Pro pricing
 * before the v1.2 switch (#844) or move the hard-launch anchor (#562).
 */
export interface PlatformFlags {
  /** #844 — facilitator-facing Pro pricing/upsell surfaces. OFF for Phase 1. */
  pro_surfaces_enabled: boolean
  /** #562 — YYYY-MM-DD. */
  hard_launch_date: string | null
  /** #1326 — is the first-booking fee waiver actually on? */
  fee_waiver_enabled: boolean
  /** #1326 — the admin-managed wording. Never rendered unless enabled. */
  fee_waiver_message: string | null
}

export const DEFAULT_PLATFORM_FLAGS: PlatformFlags = {
  pro_surfaces_enabled: false,
  hard_launch_date: null,
  // #1326 — fail closed. A network blip must never promise a waiver we do
  // not give; the cost of staying silent is a weaker sign-up prompt, the
  // cost of guessing true is a public promise the platform will not honour.
  fee_waiver_enabled: false,
  fee_waiver_message: null,
}

export async function fetchPlatformFlags(
  init?: RequestInit,
): Promise<PlatformFlags> {
  try {
    const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/platform/flags`, {
      cache: 'no-store',
      ...init,
    })
    if (!res.ok) return DEFAULT_PLATFORM_FLAGS
    const body = (await res.json()) as Partial<PlatformFlags>
    return {
      pro_surfaces_enabled: body.pro_surfaces_enabled === true,
      hard_launch_date:
        typeof body.hard_launch_date === 'string' ? body.hard_launch_date : null,
      fee_waiver_enabled: body.fee_waiver_enabled === true,
      fee_waiver_message:
        typeof body.fee_waiver_message === 'string' &&
        body.fee_waiver_message.trim() !== ''
          ? body.fee_waiver_message.trim()
          : null,
    }
  } catch {
    return DEFAULT_PLATFORM_FLAGS
  }
}
